<template>
    <div>
        <div class="row mb-4" v-show="showSamplingParameters">
            <div class="col-12 mb-2">
                <small class="text-muted">Sampling Parameters</small>
            </div>
            <div class="form-group col-6">
                <label for="temperature" class="control-label">Temperature</label>
                <!-- Disabled inputs don't submit; the hidden empty fields (here and for top_p)
                     submit the key explicitly so the backend clears the saved value instead of
                     repopulating it from the existing assistant. -->
                <input v-if="clearSamplingParameters" type="hidden" name="temperature" value="">
                <input id="temperature" type="number" step="0.01" min="0" :max="temperatureMax"
                       name="temperature" :class="['form-control', { 'is-invalid': temperatureError }]"
                       v-model="selectedTemperature" :disabled="samplingDisabled">
                <small class="form-text text-muted">Controls randomness (0-{{ temperatureMax }})</small>
                <span v-if="temperatureError" class="invalid-feedback"><strong>{{ temperatureError }}</strong></span>
            </div>
            <div class="form-group col-6">
                <label for="top_p" class="control-label">Top P</label>
                <input v-if="clearSamplingParameters" type="hidden" name="top_p" value="">
                <input id="top_p" type="number" step="0.01" min="0" max="1" name="top_p"
                       :class="['form-control', { 'is-invalid': topPError }]"
                       v-model="selectedTopP" :disabled="samplingDisabled">
                <small class="form-text text-muted">Nucleus sampling (0-1)</small>
                <span v-if="topPError" class="invalid-feedback"><strong>{{ topPError }}</strong></span>
            </div>
            <div v-if="samplingWarning" class="col-12">
                <div class="alert alert-warning py-2 mb-0"><small>{{ samplingWarning }}</small></div>
            </div>
        </div>

        <div class="row mb-4" v-show="showReasoningParameters">
            <div class="col-12 mb-2">
                <small class="text-muted">Reasoning and Output Parameters</small>
            </div>
            <div class="form-group col-6">
                <label for="reasoning_effort" class="control-label">Reasoning Effort</label>
                <input v-if="clearReasoningEffort" type="hidden" name="reasoning_effort" value="">
                <select id="reasoning_effort" v-model="selectedReasoningEffort" name="reasoning_effort"
                        :class="['form-control', { 'is-invalid': reasoningEffortError }]"
                        :disabled="reasoningDisabled">
                    <option v-for="option in reasoningOptions" :key="option.value"
                            :value="option.value" :disabled="option.disabled">
                        {{ option.label }}
                    </option>
                </select>
                <small class="form-text text-muted">Controls depth of reasoning</small>
                <span v-if="reasoningEffortError" class="invalid-feedback">
                    <strong>{{ reasoningEffortError }}</strong>
                </span>
                <div v-if="reasoningWarning" class="alert alert-warning py-2 mt-2 mb-0">
                    <small>{{ reasoningWarning }}</small>
                </div>
            </div>
            <div class="form-group col-6">
                <label for="text_verbosity" class="control-label">Text Verbosity</label>
                <input v-if="clearTextVerbosity" type="hidden" name="text_verbosity" value="">
                <select id="text_verbosity" v-model="selectedTextVerbosity" name="text_verbosity"
                        :class="['form-control', { 'is-invalid': textVerbosityError }]"
                        :disabled="textVerbosityDisabled">
                    <option v-for="option in textVerbosityOptions" :key="option.value"
                            :value="option.value" :disabled="option.disabled">
                        {{ option.label }}
                    </option>
                </select>
                <small class="form-text text-muted">Controls output length</small>
                <span v-if="textVerbosityError" class="invalid-feedback">
                    <strong>{{ textVerbosityError }}</strong>
                </span>
                <div v-if="textVerbosityWarning" class="alert alert-warning py-2 mt-2 mb-0">
                    <small>{{ textVerbosityWarning }}</small>
                </div>
            </div>
            <div v-if="capabilitiesLoading" class="col-12">
                <small class="text-muted">Checking model parameter support&hellip;</small>
            </div>
        </div>
    </div>
</template>

<script>
// Keep both option lists in sync with the value constants in
// app/Services/AI/OpenAIResponseFormatProbeService.php.
const REASONING_OPTIONS = [
    {value: '', label: 'Not Set'},
    {value: 'none', label: 'None'},
    {value: 'minimal', label: 'Minimal'},
    {value: 'low', label: 'Low'},
    {value: 'medium', label: 'Medium'},
    {value: 'high', label: 'High'},
    {value: 'xhigh', label: 'Extra High'},
    {value: 'max', label: 'Max'},
];
const TEXT_VERBOSITY_OPTIONS = [
    {value: '', label: 'Not Set'},
    {value: 'low', label: 'Low'},
    {value: 'medium', label: 'Medium'},
    {value: 'high', label: 'High'},
];
// Preserve the prior model-family behavior while capability checks are pending or indeterminate.
const LEGACY_OPENAI_MODELS = [
    'gpt-4o', 'chatgpt-4o-latest', 'gpt-4-turbo', 'gpt-4-turbo-preview',
    'gpt-4', 'gpt-4-0613', 'gpt-4-32k', 'gpt-3.5-turbo', 'gpt-3.5-turbo-16k',
];
const LEGACY_OPENAI_PREFIXES = ['gpt-4o-', 'gpt-4-', 'gpt-3.5-'];

export default {
    name: 'ModelParameterFields',
    props: {
        model: {type: String, default: ''},
        provider: {type: String, default: 'openai'},
        modelCapabilities: {type: Object, default: null},
        capabilitiesLoading: {type: Boolean, default: false},
        initialTemperature: {type: [Number, String], default: 1},
        initialTopP: {type: [Number, String], default: 1},
        initialReasoningEffort: {type: String, default: ''},
        initialTextVerbosity: {type: String, default: ''},
        temperatureError: {type: String, default: null},
        topPError: {type: String, default: null},
        reasoningEffortError: {type: String, default: null},
        textVerbosityError: {type: String, default: null},
    },
    data() {
        return {
            selectedTemperature: this.initialTemperature,
            selectedTopP: this.initialTopP,
            selectedReasoningEffort: this.initialReasoningEffort,
            selectedTextVerbosity: this.initialTextVerbosity,
            samplingValuesBeforeUnsupported: null,
        };
    },
    computed: {
        temperatureMax() {
            return this.provider === 'bedrock' ? 1 : 2;
        },
        reasoningCapability() {
            return this.modelCapabilities?.reasoning_effort || null;
        },
        textVerbosityCapability() {
            return this.modelCapabilities?.text_verbosity || null;
        },
        reasoningOptions() {
            return this.filterOptions(
                REASONING_OPTIONS,
                this.reasoningCapability,
                this.selectedReasoningEffort,
            );
        },
        textVerbosityOptions() {
            return this.filterOptions(
                TEXT_VERBOSITY_OPTIONS,
                this.textVerbosityCapability,
                this.selectedTextVerbosity,
            );
        },
        isLegacyOpenAIModel() {
            return LEGACY_OPENAI_MODELS.includes(this.model)
                || LEGACY_OPENAI_PREFIXES.some(prefix => this.model.startsWith(prefix));
        },
        showSamplingParameters() {
            if (this.provider === 'bedrock') {
                return true;
            }

            if (!this.isDefinitive(this.reasoningCapability)) {
                return this.isLegacyOpenAIModel;
            }

            return this.reasoningCapability.values.length === 0
                || this.reasoningCapability.values.includes('none');
        },
        showReasoningParameters() {
            if (this.provider === 'bedrock') {
                return false;
            }

            if (!this.isDefinitive(this.reasoningCapability)
                || !this.isDefinitive(this.textVerbosityCapability)) {
                return !this.isLegacyOpenAIModel;
            }

            return this.reasoningCapability.values.length > 0
                || this.textVerbosityCapability.values.length > 0;
        },
        reasoningDisabled() {
            return !this.showReasoningParameters
                || (this.isDefinitive(this.reasoningCapability)
                    && this.reasoningCapability.values.length === 0);
        },
        textVerbosityDisabled() {
            return !this.showReasoningParameters
                || (this.isDefinitive(this.textVerbosityCapability)
                    && this.textVerbosityCapability.values.length === 0);
        },
        clearReasoningEffort() {
            return this.provider === 'bedrock'
                || (!this.capabilitiesLoading
                    && this.isDefinitive(this.reasoningCapability)
                    && this.reasoningCapability.values.length === 0);
        },
        clearTextVerbosity() {
            return this.provider === 'bedrock'
                || (!this.capabilitiesLoading
                    && this.isDefinitive(this.textVerbosityCapability)
                    && this.textVerbosityCapability.values.length === 0);
        },
        samplingUnsupported() {
            if (this.provider === 'bedrock') {
                return false;
            }

            if (!this.isDefinitive(this.reasoningCapability)
                || this.reasoningCapability.values.length === 0) {
                return false;
            }

            if (!this.reasoningCapability.values.includes('none')) {
                return true;
            }

            return this.selectedReasoningEffort !== ''
                && this.selectedReasoningEffort !== 'none';
        },
        samplingDisabled() {
            return !this.showSamplingParameters || this.samplingUnsupported;
        },
        clearSamplingParameters() {
            return !this.capabilitiesLoading && this.samplingUnsupported;
        },
        samplingWarning() {
            if (this.capabilitiesLoading || !this.samplingUnsupported) return null;

            return this.reasoningCapability.values.includes('none')
                ? 'Set Reasoning Effort to None to use Temperature and Top P with this model.'
                : 'Temperature and Top P are not supported by this reasoning model.';
        },
        reasoningWarning() {
            if (this.capabilitiesLoading) return null;

            return this.unsupportedValueWarning(
                this.reasoningCapability,
                this.selectedReasoningEffort,
                'reasoning effort',
            );
        },
        textVerbosityWarning() {
            if (this.capabilitiesLoading) return null;

            return this.unsupportedValueWarning(
                this.textVerbosityCapability,
                this.selectedTextVerbosity,
                'text verbosity',
            );
        },
    },
    watch: {
        modelCapabilities: {
            immediate: true,
            handler() {
                this.reconcileModelParameters();
            },
        },
        provider() {
            this.reconcileModelParameters();
        },
        selectedReasoningEffort() {
            this.reconcileSamplingParameters();
        },
        selectedTemperature() {
            this.reconcileSamplingParameters();
        },
        selectedTopP() {
            this.reconcileSamplingParameters();
        },
    },
    methods: {
        isDefinitive(capability) {
            return capability && !capability.indeterminate && Array.isArray(capability.values);
        },
        filterOptions(options, capability, selectedValue) {
            if (!this.isDefinitive(capability)) return options;

            const supported = options.filter(option => option.value === ''
                || capability.values.includes(option.value));

            if (!selectedValue || supported.some(option => option.value === selectedValue)) {
                return supported;
            }

            const selected = options.find(option => option.value === selectedValue);
            return [
                ...supported,
                {value: selectedValue, label: selected?.label || selectedValue, disabled: true},
            ];
        },
        unsupportedValueWarning(capability, value, label) {
            if (!this.isDefinitive(capability)) {
                return null;
            }

            if (value && !capability.values.includes(value)) {
                return `The saved ${label} is not supported by this model. Select a supported value.`;
            }

            return capability.values.length === 0 ? capability.reason : null;
        },
        hasSamplingParameters() {
            return [this.selectedTemperature, this.selectedTopP]
                .some(value => value !== '' && value !== null && value !== undefined);
        },
        reconcileModelParameters() {
            if (this.provider === 'bedrock') {
                this.selectedReasoningEffort = '';
                this.selectedTextVerbosity = '';
                this.restoreSamplingValues();

                return;
            }

            this.selectedReasoningEffort = this.supportedValue(
                this.reasoningCapability,
                this.selectedReasoningEffort,
            );
            this.selectedTextVerbosity = this.supportedValue(
                this.textVerbosityCapability,
                this.selectedTextVerbosity,
            );
            this.reconcileSamplingParameters();
        },
        supportedValue(capability, value) {
            if (!this.isDefinitive(capability)
                || !value
                || capability.values.includes(value)) {
                return value;
            }

            return '';
        },
        reconcileSamplingParameters() {
            const capability = this.reasoningCapability;
            if (!this.isDefinitive(capability)) {
                return;
            }

            if (capability.values.length === 0) {
                this.restoreSamplingValues();
                return;
            }

            if (!capability.values.includes('none')) {
                this.rememberSamplingValues();
                this.clearSamplingValues();
                return;
            }

            if (['', 'none'].includes(this.selectedReasoningEffort)
                && this.restoreSamplingValues()) {
                this.selectedReasoningEffort = 'none';
                return;
            }

            if (this.selectedReasoningEffort === '' && this.hasSamplingParameters()) {
                this.selectedReasoningEffort = 'none';
                return;
            }

            if (this.selectedReasoningEffort !== '' && this.selectedReasoningEffort !== 'none') {
                this.clearSamplingValues();
            }
        },
        rememberSamplingValues() {
            if (!this.hasSamplingParameters() || this.samplingValuesBeforeUnsupported) {
                return;
            }

            this.samplingValuesBeforeUnsupported = {
                temperature: this.selectedTemperature,
                topP: this.selectedTopP,
            };
        },
        restoreSamplingValues() {
            if (this.hasSamplingParameters() || !this.samplingValuesBeforeUnsupported) {
                return false;
            }

            this.selectedTemperature = this.samplingValuesBeforeUnsupported.temperature;
            this.selectedTopP = this.samplingValuesBeforeUnsupported.topP;
            this.samplingValuesBeforeUnsupported = null;

            return true;
        },
        clearSamplingValues() {
            this.selectedTemperature = '';
            this.selectedTopP = '';
        },
    },
};
</script>
